// src/native/ui/NativeAppShell.jsx
//
// One component for INT to mount, so the native UX work costs App.jsx a single
// import and a single line inside the existing tree.
//
// It composes:
//   - native chrome (nativeChrome.js): the CSS that stops the webview looking
//     like a webview,
//   - the splash controller (nativeSplash.js): hide the native splash the frame
//     after the first real paint, with a hard failsafe,
//   - the link guard (nativeLinks.jsx): internal target="_blank" links become
//     route changes, external links open in-app instead of in Safari.
//
// On the web every one of those is a no-op and this component renders null.
// isNative() reads a global the Capacitor bridge injects, so on the web the
// whole subtree below is dead code that never runs.

import { useEffect } from 'react';
import { isNative } from '@/lib/platform';
import { applyNativeChrome, removeNativeChrome } from './nativeChrome';
import { armNativeSplashFailsafe, scheduleNativeSplashHide } from './nativeSplash';
import { NativeLinkGuard } from './nativeLinks';

const NativeAppShell = () => {
  // Read once per render rather than in the effect body, so the hook list is
  // identical on web and native and the rules of hooks are respected.
  const native = isNative();

  useEffect(() => {
    if (!native) return undefined;

    applyNativeChrome();
    // Arm the ceiling before scheduling the normal hide: if a route chunk hangs
    // or a render throws, the user still gets out of the splash.
    armNativeSplashFailsafe();
    scheduleNativeSplashHide();

    return () => removeNativeChrome();
  }, [native]);

  if (!native) return null;

  return <NativeLinkGuard />;
};

export default NativeAppShell;
